import { useRuns, useServices, useTasks } from "../../lib/queries";
import Sparkline, { fakeSparkline } from "../cockpit/Sparkline";
import { relativeTime } from "../../lib/format";

interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  delta?: { sign: "up" | "down" | "flat"; text: string };
  data?: number[];
  stroke?: string;
}

function KpiCard({ label, value, hint, delta, data, stroke }: KpiCardProps) {
  return (
    <div
      className="aero-glass flex flex-col px-4 py-3"
      style={{ borderRadius: 20 }}
    >
      <div className="flex items-center justify-between">
        <span
          className="font-eyebrow"
          style={{
            fontSize: 9,
            fontWeight: 800,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            color: "var(--hot-pink)",
          }}
        >
          {label}
        </span>
        {data ? <Sparkline data={data} width={52} height={16} stroke={stroke} className="pink-glow" /> : null}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span
          className="holo-text tabular"
          style={{
            fontFamily: "Fraunces, serif",
            fontStyle: "italic",
            fontWeight: 700,
            fontSize: 30,
            lineHeight: 1,
          }}
        >
          {value}
        </span>
        {delta ? (
          <span
            className="font-eyebrow"
            style={{
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color:
                delta.sign === "up"
                  ? "#7ec699"
                  : delta.sign === "down"
                    ? "#cf6679"
                    : "var(--plum)",
            }}
          >
            {delta.text}
          </span>
        ) : null}
      </div>
      {hint ? (
        <span
          className="mt-1 text-xs"
          style={{
            fontFamily: "Fraunces, serif",
            fontStyle: "italic",
            color: "var(--plum)",
            opacity: 0.7,
          }}
        >
          {hint}
        </span>
      ) : null}
    </div>
  );
}

export default function KpiStrip() {
  const { data: runs = [] } = useRuns();
  const { data: tasks = [] } = useTasks();
  const { data: services = [] } = useServices();

  const lastRun = runs[0];
  const openTasks = tasks.filter((t) => t.status === "todo" || t.status === "in_progress").length;
  const blockedTasks = tasks.filter((t) => t.status === "blocked").length;
  const totalSpend = services.reduce((s, sv) => s + (sv.monthly_cost_usd ?? 0), 0);
  const healthyServices = services.filter((s) => s.status === "healthy").length;

  return (
    <div className="grid grid-cols-4 gap-3 px-3 pt-3">
      <KpiCard
        label="Last run"
        value={lastRun ? relativeTime(lastRun.started_at) : "—"}
        hint={lastRun ? `${lastRun.agent} · ${lastRun.status}` : "no runs yet"}
        data={fakeSparkline("last-run", 7)}
        stroke="#FF1FA5"
      />
      <KpiCard
        label="Open tasks"
        value={`${openTasks}`}
        hint={`${blockedTasks} blocked`}
        delta={
          blockedTasks > 0
            ? { sign: "down", text: `${blockedTasks} blk` }
            : { sign: "flat", text: "—" }
        }
        data={fakeSparkline("tasks", 7)}
        stroke="#FF1FA5"
      />
      <KpiCard
        label="Services healthy"
        value={`${healthyServices}/${services.length}`}
        hint={`${services.length - healthyServices} flagged`}
        data={fakeSparkline("services", 7)}
        stroke="#FF1FA5"
      />
      <KpiCard
        label="Monthly cost"
        value={`$${Math.round(totalSpend)}`}
        hint="across all services"
        delta={{ sign: "flat", text: "= mo/mo" }}
        data={fakeSparkline("cost", 7)}
        stroke="#FF1FA5"
      />
    </div>
  );
}
